import { useEffect, useRef, useState } from 'react';
import {
  PULSE_SHOCKWAVE_SOUND_PATH,
  SWARM_COLLAPSE_SOUND_PATH,
  PULSE_SHOCKWAVE_DURATION_MS,
} from '../config/pulseShockwaveEffect';

function mapHitsToViewerSlots(hits, viewerUserId) {
  if (!Array.isArray(hits)) return [];
  return hits.map((h) => {
    const isOwnerViewer = String(h.targetPlayerId) === String(viewerUserId);
    const prefix = isOwnerViewer ? 'your' : 'opponent';
    return {
      zone: `${prefix}_${h.zone}`,
      index: h.slotIndex,
      damage: typeof h.damage === 'number' ? h.damage : Number(h.damage) || 0,
    };
  });
}

function isPulseStyleShockwaveStrategem(s) {
  if (!s || s.instanceId == null) return false;
  const id = Number(s.id);
  if (id !== 110 && id !== 51 && id !== 57) return false;
  const t = (s.type && String(s.type)) || '';
  if (!t) return true;
  return /strategem/i.test(t);
}

/**
 * Strategem 110 (Pulse Shockwave), 51 (Culling), 57 (Swarm Collapse): PulseShockwaveEffect (`pulseShockwaveFx`).
 * Sounds: 110 default shockwave; 51 shockwave; 57 swarm_collapse.mp3.
 */
export function usePulseShockwave(gameState, viewerUserId) {
  const [activePulseShockwaves, setActivePulseShockwaves] = useState([]);
  const processedRef = useRef(new Set());
  const seededRef = useRef(false);
  const gameIdRef = useRef(null);

  useEffect(() => {
    if (!gameState?.state || viewerUserId == null) return;

    const gid = gameState.gameId ?? null;
    if (gid != null && gameIdRef.current !== gid) {
      gameIdRef.current = gid;
      seededRef.current = false;
      processedRef.current = new Set();
      setActivePulseShockwaves([]);
    }

    if (!seededRef.current) {
      Object.keys(gameState.state).forEach((pid) => {
        const pl = gameState.state[pid];
        pl?.completedOrders?.forEach((st) => {
          if (isPulseStyleShockwaveStrategem(st)) {
            processedRef.current.add(`pulseFx-${st.id}-${pid}-${st.instanceId}`);
          }
        });
      });
      seededRef.current = true;
      return;
    }

    Object.keys(gameState.state).forEach((casterId) => {
      const player = gameState.state[casterId];
      if (!player?.completedOrders) return;

      player.completedOrders.forEach((strategem) => {
        if (!isPulseStyleShockwaveStrategem(strategem)) return;

        const fx = strategem.pulseShockwaveFx;
        if (!fx || !Array.isArray(fx.hits)) return;

        const sid = Number(strategem.id);
        const dedupeKey = `pulseFx-${sid}-${casterId}-${strategem.instanceId}`;
        if (processedRef.current.has(dedupeKey)) return;

        processedRef.current.add(dedupeKey);

        const direction = String(casterId) === String(viewerUserId) ? 'up' : 'down';
        const variant =
          sid === 51 ? 'culling' : sid === 57 ? 'swarm' : 'pulse';
        const viewerHits = variant === 'culling' ? [] : mapHitsToViewerSlots(fx.hits, viewerUserId);
        const animKey = `pulseFx-${sid}-${strategem.instanceId}-${Date.now()}`;

        setActivePulseShockwaves((prev) => [
          ...prev,
          {
            key: animKey,
            direction,
            viewerHits,
            variant,
            animationKey: animKey,
          },
        ]);

        try {
          const soundPath =
            sid === 57 ? SWARM_COLLAPSE_SOUND_PATH : PULSE_SHOCKWAVE_SOUND_PATH;
          const audio = new Audio(soundPath);
          audio.volume = 0.68;
          audio.play().catch(() => {});
        } catch (_) {}

        window.setTimeout(() => {
          setActivePulseShockwaves((prev) => prev.filter((p) => p.key !== animKey));
        }, PULSE_SHOCKWAVE_DURATION_MS + 100);
      });
    });

    if (processedRef.current.size > 120) {
      const keys = Array.from(processedRef.current);
      keys.slice(0, keys.length - 80).forEach((k) => processedRef.current.delete(k));
    }
  }, [gameState, gameState?.stateVersion, viewerUserId]);

  const completePulseShockwave = (key) => {
    setActivePulseShockwaves((prev) => prev.filter((p) => p.key !== key));
  };

  return { activePulseShockwaves, completePulseShockwave };
}

export default usePulseShockwave;
